import { useRouterState } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";

type Patch = Record<string, string | undefined | null>;

/**
 * URL-state filters. Subscribes via `useRouterState` so changes elsewhere
 * trigger a re-render, and writes via `history.replaceState` so we sidestep
 * TanStack Router's typed-search reducer (the root route has no schema).
 *
 * Filters are flat string KV pairs persisted as `?k=v&...`. Empty values are
 * dropped on write so the URL stays clean.
 */
export function useUrlFilters<T extends Record<string, string | undefined>>(): readonly [T, (patch: Patch) => void] {
  // Subscribing to `searchStr` is enough — the router updates this whenever
  // the URL search component changes via push/replaceState.
  const searchStr = useRouterState({ select: (s) => s.location.searchStr });

  const filters = useMemo<T>(() => {
    const out: Record<string, string> = {};
    const params = new URLSearchParams(searchStr);
    for (const [k, v] of params) out[k] = v;
    return out as T;
  }, [searchStr]);

  const update = useCallback(
    (patch: Patch) => {
      const params = new URLSearchParams(window.location.search);
      for (const [k, v] of Object.entries(patch)) {
        if (v === undefined || v === "" || v === null) {
          params.delete(k);
        } else {
          params.set(k, v);
        }
      }
      const qs = params.toString();
      const url =
        window.location.pathname +
        (qs ? `?${qs}` : "") +
        window.location.hash;
      window.history.replaceState({}, "", url);
      // Manually nudge React: history.replaceState doesn't fire popstate, but
      // TanStack Router's history adapter listens to replaceState calls it
      // initiated. To make sure the app re-renders, dispatch a popstate.
      window.dispatchEvent(new PopStateEvent("popstate"));
    },
    [],
  );

  return [filters, update] as const;
}
